import React, { useState } from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import { makeStyles, withStyles } from '@material-ui/core/styles';
import { withWidth } from '@material-ui/core';
import { T, t } from '@configuratorware/configurator-frontendgui/Framework/i18n';
import customClassName from '@configuratorware/configurator-frontendgui/App/Utils/customClassName';
import ProductPart from '@configuratorware/configurator-frontendgui/App/Modules/Creator/Components/ProductPart';
import DefaultProductPartsList from '@configuratorware/configurator-frontendgui/App/Modules/Creator/Components/ProductPartsList';
import { useSelector } from 'react-redux';
import { Services } from '@configuratorware/configurator-frontendgui/App/ServiceLocator';
import { removeHiddenComponents, withEnabledState } from '../../Reducers/Configurator/Selectors';

const CustomProductPart = withStyles(() => ({
  root: ({ optionclassification }) => {
    if (!optionclassification.enabled) {
      return { opacity: 0.5 };
    }
    return optionclassification.disabled ? { opacity: 0.5 } : {};
  },
  rootTypography: {},
  content: ({ optionclassification }) => {
    if (!optionclassification.enabled) {
      return { cursor: 'default', pointerEvents: 'none' };
    }
    return {};
  },
}))((props) => (
  <ProductPart
    {...props}
    onSelect={
      props.optionclassification?.disabled
        ? () =>
            Services.ui.reportError(t(`componentDisabledMessage.${props.optionclassification?.identifier}`))
        : props.optionclassification?.enabled
          ? props.onSelect
          : () => null
    }
  />
));

const MobileProductPart = withStyles(() => ({
  root: {
    width: '100%',
  },
  content: {
    '&.horizontal': {
      width: '100%',
    },
  },
  rootTypography: {
    whiteSpace: 'nowrap',
  },
}))(CustomProductPart);

const useStyles = makeStyles(
  (theme) => {
    return {
      root: {
        display: 'flex',
        overflowX: 'auto',
        '&.horizontal': {
          position: 'absolute',
          bottom: 8,
          height: 'auto',
          // the available width (see container) is reduced by the width of the two arrows
          width: 'calc(100% - 72px)',
          [theme.breakpoints.down('xs')]: {
            width: 'calc(100% - 32px)',
            padding: '0 16px',
          },
        },
        '&.vertical': {
          float: 'left',
          height: '100%',
          width: 'auto',
          marginLeft: theme.creator.productPartsList.marginLeft,
          paddingTop: theme.creator.productPartsList.paddingTop,
        },
      },
    };
  },
  { name: 'ProductPartsList' }
);

const MobileProductPartsList = ({
  displaySwitchedOptions,
  onSelect,
  optionclassifications,
  selectedoptionclassification,
  showStartHere,
  vertical,
  CallToActionComponent,
  width,
  optionListVisible,
  onCloseSelectedPart,
}) => {
  const classes = useStyles();
  const [anchorRef, setAnchorRef] = useState(null);

  return (
    <div className={clsx(classes.root, customClassName('components'))}>
      {optionclassifications.map((option, index) => {
        const refProps = {
          ...(index === 0 && { setAnchorRef }),
        };

        return (
          <MobileProductPart
            key={'optionclassificationitem' + option.identifier}
            displaySwitchedOptions={displaySwitchedOptions}
            onSelect={onSelect}
            optionclassification={option}
            selectedOption={selectedoptionclassification}
            verticalMode={vertical}
            optionListVisible={optionListVisible}
            onCloseSelected={onCloseSelectedPart}
            {...refProps}
          />
        );
      })}
      {showStartHere && width !== 'xs' && (
        <CallToActionComponent
          offsetDirection={vertical ? 'right' : 'top'}
          anchorRef={anchorRef}
          perpendicularOffset={vertical ? -15 : 0}
          offset={15}
          label={T('creator.startHere')}
          className={customClassName('begin-here-creator')}
        />
      )}
    </div>
  );
};

MobileProductPartsList.defaultProps = {
  onSelect: () => {},
  optionclassifications: [],
  selectedoptionclassification: {},
  showStartHere: false,
  vertical: true,
};

MobileProductPartsList.propTypes = {
  onSelect: PropTypes.func,
  optionclassifications: PropTypes.array,
  selectedoptionclassification: PropTypes.object,
  showStartHere: PropTypes.bool,
  vertical: PropTypes.bool,
  displaySwitchedOptions: PropTypes.bool,
  CallToActionComponent: PropTypes.elementType,
  width: PropTypes.string,
  optionListVisible: PropTypes.bool,
};

export const ProductPartsList = (props) => {
  const Component = props.vertical ? DefaultProductPartsList : MobileProductPartsList;
  const configurator = useSelector(({ configurator }) => configurator);
  const { filteredOptionclassifications, checkedOptionclassifications } = configurator;
  const optionclassifications = withEnabledState(
    removeHiddenComponents(filteredOptionclassifications),
    checkedOptionclassifications
  );
  return (
    <Component
      {...props}
      optionclassifications={optionclassifications}
      ProductPartComponent={CustomProductPart}
    />
  );
};

export default withWidth({ initialWidth: 'lg' })(ProductPartsList);
